import React from 'react';

import { useTranslate } from '@root/lib/hooks/useTranslation';

import { type TransTypes as Types } from './Trans.types';

/**
 * Common HTML components for `Trans` component interpolations
 * @export
 */
export const commonI18nComponents = {
	html_bold: <b />,
	b: <b />,
	html_italic: <i />,
	i: <i />,
	html_span: <span />,
	span: <span />,
	html_paragraph: <p />,
	p: <p />,
	html_anchor: <a className="text-indigo-500 underline" />,
	a: <a className="text-indigo-500 underline" />,
	html_strong: <strong />,
	strong: <strong />,
	html_small: <small />,
	small: <small />,
	br: <br />,
} as const;

/**
 * Trans component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const Trans: React.FunctionComponent<Types.Props> = ({
	i18nKey,
	values,
	components = commonI18nComponents,
	fallback,
	defaultTrans,
	tFunc,
	scope,
	namespace,
	count,
	parent: Parent,
	...props
}) => {
	const { lang, ...tFunctions } = useTranslate(namespace, scope);
	const t = tFunc ?? tFunctions.t;

	/**
	 * Memoize the transformation
	 */
	const content = React.useMemo(() => {
		const text = t(i18nKey, { ...values, count }, { fallback, default: defaultTrans });
		if (!text) return text;

		if (!components || (components as any[]).length === 0) return Array.isArray(text) ? text.map((item) => item) : text;

		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		if (Array.isArray(text)) return text.map((item) => formatElements(item, components));

		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		return formatElements(text, components);
	}, [t, i18nKey, values, count, fallback, defaultTrans, components]);

	const Tag = Parent ?? 'span';
	if (typeof content === 'string') {
		return <Tag {...props} dangerouslySetInnerHTML={{ __html: content }} />;
	}

	return <Tag {...props}>{content}</Tag>;
};

/**
 * Component display name
 * @type {ComponentDisplayName}
 */
Trans.displayName = 'components.Trans';

/** Extracted from next-translate as it's not exported */
/** NOTE: this tagParsingRegex does not support HTML tags with attributes */
/** Updated to handle HTML tags with attributes */
export const tagParsingRegex = /<(\w+)(\s+[^>]*)? *>(.*?)<\/\1 *>|<(\w+)(\s+[^>]*)? *\/>/;
const nlRe = /(?:\r\n|\r|\n)/g;

function getElements(parts: Array<string | undefined>): Array<[string | undefined, string, string, string | undefined]> {
	if (!parts.length) return [];

	// Extract tag name, attributes, children, and content after tag
	const [paired, attributes, children, unpaired, unpairedAttributes, after] = parts.slice(0, 6);

	// @ts-ignore
	return [[(paired || unpaired) as string, attributes || '', children || ('' as string), after]].concat(getElements(parts.slice(6, parts.length)));
}

export function formatElements(
	value: string,
	elements: React.ReactElement<any>[] | Record<string, React.ReactElement<any>> = [],
): string | React.ReactNode[] {
	const parts = value.replace(nlRe, '').split(tagParsingRegex);
	if (parts.length === 1) return value;

	const tree = [];
	const before = parts.shift();
	if (before) tree.push(before);

	getElements(parts).forEach(([key, attributes, children, after], realIndex: number) => {
		// Use the key to get the appropriate React element from the provided elements or default to a fragment
		const element =
			// @ts-ignore
			elements[key as string] || <React.Fragment />;

		tree.push(
			React.cloneElement(
				element,
				// eslint-disable-next-line @typescript-eslint/no-use-before-define
				{ key: realIndex, ...parseAttributes(attributes) }, // Parse and pass attributes to the element
				// Format children for paired tags
				children ? formatElements(children, elements) : element.props.children,
			),
		);

		// Push the content after the tag (if any)
		if (after) tree.push(after);
	});

	return tree;
}

/** Helper function to parse attributes into an object */
function parseAttributes(attributeString: string): Record<string, string> {
	const attributes: Record<string, string> = {};

	if (!attributeString) return attributes;

	// Regex to match attributes in the form key="value" or key='value' or just key
	const attrRegex = /(\w+)=["']?((?:.(?!["']?\s+(?:\S+)=|[>"']))+.)["']?/g;
	let match;

	while ((match = attrRegex.exec(attributeString))) {
		const [, key, value] = match;
		// @ts-ignore
		attributes[key] = value;
	}

	return attributes;
}
